import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiService, ApiError, User } from '../services/api';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/** Which protocol the browser last used for an API call (needs the
 *  server's Timing-Allow-Origin; '?' until that ships or when hidden). */
const lastApiProtocol = (): string => {
  const entries = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
  for (let i = entries.length - 1; i >= 0; i--) {
    if (entries[i].name.startsWith(API_BASE)) return entries[i].nextHopProtocol || '?';
  }
  return '?';
};

/** Probe the API after a login POST died in the browser and name the
 *  layer that failed. Never throws. */
async function describeNetworkFailure(): Promise<string> {
  const at = new Date().toLocaleTimeString('en-US', { hour12: false });
  let health: string;
  try {
    const r = await fetch(`${API_BASE}/health`, { cache: 'no-store' });
    health = `health ${r.status}`;
  } catch (e) {
    health = `health unreachable (${e instanceof Error ? e.message : String(e)})`;
  }
  const proto = lastApiProtocol();
  const online = typeof navigator !== 'undefined' && navigator.onLine === false ? 'offline' : 'online';
  const detail = `login request never reached the API · ${health} · proto ${proto} · ${online} · ${at}`;
  // Structured copy for the console / a bug report.
  console.warn('[login-diagnostic]', { api: API_BASE, health, proto, online, at, ua: navigator.userAgent });
  return `Login failed — ${detail}`;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('authToken');
      if (token) {
        try {
          const userData = await apiService.getCurrentUser();
          setUser(userData);
        } catch (error) {
          console.error('Failed to get current user:', error);
          localStorage.removeItem('authToken');
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await apiService.login({ email, password });
      localStorage.setItem('authToken', response.token);
      setUser(response.user);
    } catch (error) {
      if (error instanceof ApiError) {
        throw new Error(error.message);
      }
      // The server never answered (network/CORS/blocked) — say WHERE it
      // failed instead of a bare "Login failed", so an intermittent
      // failure diagnoses itself without DevTools (10/25-26 incidents).
      throw new Error(await describeNetworkFailure());
    }
  };

  const logout = async () => {
    try {
      await apiService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('authToken');
      setUser(null);
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
